import { Injectable } from '@angular/core';
import { DateInputDialogService } from '../shared/date-input-dialog/date-input-dialog.service';

/**
 * Servicio compartido para generar payloads de eliminación masiva.
 * Centraliza la lógica común entre SICOM_SUMMARY y MICROFINANCE.
 */
@Injectable({
  providedIn: 'root'
})
export class EliminationPayloadService {

  constructor(private dateInputDialogService: DateInputDialogService) { }

  /**
   * Genera el payload para la eliminación masiva (DIS) según el tipo especificado por el usuario.
   * Aplica 'x' o null a los campos de indicadores según las reglas del negocio.
   * @param selectedItems Los items seleccionados.
   * @param type El tipo de eliminación a aplicar.
   * @param isMassiveSelection Indica si es una selección masiva.
   * @param startDateValue Valor opcional para startDate (usado en tipos FechaDesde). Si no se proporciona y es necesario, se solicitará al usuario.
   * @returns Una Promise que resuelve el payload listo para ser enviado al servicio DIS, o null si el usuario cancela.
   */
  public async generateEliminationPayload(
    selectedItems: any[],
    type: string,
    isMassiveSelection: boolean,
    startDateValue?: string
  ): Promise<any> {
    const itemsToUpdate = selectedItems.map(item => JSON.parse(JSON.stringify(item)));
    const X_VALUE = "x";
    const NULL_VALUE = null;

    // Lógica para operationInd que aplica SÓLO a las opciones de Proveedor
    const operationIndValue = isMassiveSelection ? X_VALUE : NULL_VALUE;

    // Usar for...of en lugar de forEach para soportar async/await
    for (const item of itemsToUpdate) {
      // En caso de que se necesite anular un campo, se asigna 'null'.
      // Los campos no especificados se dejan con su valor original, ya que el servicio
      // DIS solo toma en cuenta los campos que están presentes para su modificación.

      switch (type) {

        case 'SuplantacionIdentidad':
          // Establecer los campos especificados a 'x'
          item.impersonationInd = X_VALUE;
          item.historicalRecordInd = X_VALUE;
          item.eliminationInd = X_VALUE;

          // Establecer los campos especificados a null
          item.supplierInd = NULL_VALUE;
          item.cancellationInd = NULL_VALUE;
          item.expiredRecordsInd = NULL_VALUE;
          item.currentRegistrationInd = NULL_VALUE;
          item.startDate = NULL_VALUE;

          // Lógica: operationInd es 'x' si es selección masiva, 'null' si no lo es.
          item.operationInd = operationIndValue;
          break;

        case 'CancelacionPagoCarteraVencidaAlDia':
          // Establecer los campos especificados a 'x'
          item.cancellationInd = X_VALUE;
          item.currentRegistrationInd = X_VALUE;
          item.eliminationInd = X_VALUE;

          // El usuario especificó: 'expiredRecordsInd en null'.
          item.expiredRecordsInd = NULL_VALUE;

          // Establecer los campos especificados a null
          item.supplierInd = NULL_VALUE;
          item.impersonationInd = NULL_VALUE;
          item.historicalRecordInd = NULL_VALUE;
          item.startDate = NULL_VALUE;

          // Lógica: operationInd es 'x' si es selección masiva, 'null' si no lo es.
          item.operationInd = operationIndValue;
          break;

        case 'CancelacionPagoCarteraSoloVencida':
          // Establecer los campos especificados a 'x'
          item.cancellationInd = X_VALUE;
          item.currentRegistrationInd = X_VALUE;
          item.expiredRecordsInd = X_VALUE;

          // El usuario especificó: 'eliminationInd en null'.
          item.eliminationInd = NULL_VALUE;

          // Establecer los campos especificados a null
          item.supplierInd = NULL_VALUE;
          item.impersonationInd = NULL_VALUE;
          item.historicalRecordInd = NULL_VALUE;
          item.startDate = NULL_VALUE;

          // Lógica: operationInd es 'x' si es selección masiva, 'null' si no lo es.
          item.operationInd = operationIndValue;
          break;

        case 'ProveedorPeriodoActualCarteraVencidaAlDia':
        case 'ProveedorPeriodoActualCarteraSoloVencida':
        case 'ProveedorPeriodoHistoricoCarteraVencidaAlDia':
        case 'ProveedorPeriodoHistoricoCarteraSoloVencida':
        case 'ProveedorPeriodoFechaDesdeCarteraVencidaAlDia':
        case 'ProveedorPeriodoFechaDesdeCarteraSoloVencida':
          // Lógica de Proveedor
          item.supplierInd = X_VALUE;
          item.impersonationInd = NULL_VALUE;
          item.cancellationInd = NULL_VALUE;

          // Lógica: operationInd es 'x' si es selección masiva, 'null' si no lo es.
          item.operationInd = operationIndValue;

          // Lógica específica para Vencida/AlDía o Solo Vencida
          if (type.includes('CarteraVencidaAlDia')) {
            item.eliminationInd = X_VALUE;
            item.expiredRecordsInd = NULL_VALUE;
          } else if (type.includes('CarteraSoloVencida')) {
            item.eliminationInd = NULL_VALUE;
            item.expiredRecordsInd = X_VALUE;
          }

          // Lógica específica para Periodo (Actual, Histórico, Fecha Desde)
          if (type.includes('PeriodoActual')) {
            item.startDate = NULL_VALUE;
            item.currentRegistrationInd = X_VALUE;
            item.historicalRecordInd = NULL_VALUE;
          } else if (type.includes('PeriodoHistorico')) {
            item.startDate = NULL_VALUE;
            item.currentRegistrationInd = NULL_VALUE;
            item.historicalRecordInd = X_VALUE;
          } else if (type.includes('FechaDesde')) {
            // Si no se proporcionó startDateValue, solicitar al usuario mediante modal
            if (!startDateValue) {
              // Pasar información del registro actual al modal
              const operationNumber = item.operationNumber || '';
              const identifierNumber = item.identifierNumber || '';

              const userInput = await this.dateInputDialogService.promptDate(
                'Operación a eliminar',
                'Ingrese fecha de eliminación',
                'YYYYMMDD',
                operationNumber,
                identifierNumber
              );

              if (userInput === null || userInput.trim() === '') {
                // Usuario canceló o no ingresó nada
                return null; // Retornar null para indicar cancelación
              }
              item.startDate = userInput;
            } else {
              item.startDate = startDateValue;
            }
            item.currentRegistrationInd = NULL_VALUE;
            item.historicalRecordInd = NULL_VALUE;
          }
          break;

        default:
          // En otro caso, no hacer modificaciones.
          break;
      }
    }

    return itemsToUpdate;
  }
}
